import { useEffect } from "react";

/**
 * @astrana/ui defines its CSS variables AND its utility classes scoped under
 * `.astrana-ui` (e.g. `.astrana-ui .bg-card{…}`). The lib's dialogs/popovers
 * are Radix-portaled to `<body>` — outside the `.astrana-ui` subtree that
 * MemberProfile renders — so their surfaces render unstyled/transparent.
 *
 * Rather than class `<body>` (which leaks the lib's typography/palette onto the
 * whole app), we tag ONLY the Radix portal wrapper `<div>` that Radix inserts
 * directly under `<body>`. That gives the portaled content an `.astrana-ui`
 * ancestor — scoping just the dialog/popover subtree, leaving app chrome
 * untouched. We tag the wrapper (not the content node itself, and we never
 * reparent) so React's portal reconciliation is unaffected.
 */
const PORTAL_CONTENT_SELECTOR =
  '[role="dialog"],[role="alertdialog"],[data-radix-popper-content-wrapper]';

function scopeBodyLevelAncestor(el: Element): void {
  // Walk up to the direct child of <body> (the Radix portal wrapper), then add
  // the scope class there so the content node and its descendants resolve the
  // lib's `.astrana-ui …` rules.
  let node: Element = el;
  while (node.parentElement && node.parentElement !== document.body) {
    node = node.parentElement;
  }
  if (node instanceof HTMLElement && !node.classList.contains("astrana-ui")) {
    node.classList.add("astrana-ui");
  }
}

function scan(root: ParentNode): void {
  root
    .querySelectorAll?.(PORTAL_CONTENT_SELECTOR)
    .forEach(scopeBodyLevelAncestor);
}

export function useAstranaPortalScope(): void {
  useEffect(() => {
    scan(document);
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((n) => {
          if (!(n instanceof HTMLElement)) return;
          if (n.matches?.(PORTAL_CONTENT_SELECTOR)) {
            scopeBodyLevelAncestor(n);
          }
          scan(n);
        });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
}
